import { useState, useEffect, Fragment } from 'react';
import { useAuth } from '../hooks/useAuth.jsx';
import {
  getOrg, updateOrg, addOrgMember, updateOrgMember, removeOrgMember,
  connectDomain, getPlatformOrgs, getPlatformUsers, createPlatformOrg,
  setSuperAdmin, getOrgAddresses
} from '../services/emailApi';

function MemberRow({ member: m, onRoleChange, onRemove }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(m.displayName || '');

  async function saveName() {
    if (name.trim() && name !== m.displayName) {
      try {
        // Update user display name via admin API
        await updateOrgMember(m.orgId || m.id, m.id, { display_name: name.trim() });
      } catch { /* silent */ }
    }
    setEditing(false);
  }

  return (
    <div className="flex items-center justify-between py-3 group">
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center flex-shrink-0">
          <span className="text-white text-xs font-semibold">{(m.displayName || m.email || '?')[0].toUpperCase()}</span>
        </div>
        <div className="min-w-0 flex-1">
          {editing ? (
            <input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              onBlur={saveName}
              onKeyDown={(e) => { if (e.key === 'Enter') saveName(); if (e.key === 'Escape') setEditing(false); }}
              className="text-sm font-medium text-gray-900 border-b border-blue-400 outline-none bg-transparent w-full"
            />
          ) : (
            <p className="text-sm font-medium text-gray-900 cursor-pointer hover:text-blue-600" onClick={() => setEditing(true)}>
              {m.displayName || <span className="text-gray-400 italic">Set name</span>}
              <span className="text-gray-300 text-[10px] ml-1 opacity-0 group-hover:opacity-100">edit</span>
            </p>
          )}
          <p className="text-xs text-gray-500 truncate">{m.email}</p>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <select
          value={m.role}
          onChange={(e) => onRoleChange(m.id, e.target.value)}
          className="rounded-lg border border-gray-200 px-2 py-1 text-xs bg-white"
        >
          <option value="member">Member</option>
          <option value="admin">Admin</option>
          <option value="owner">Owner</option>
        </select>
        <button onClick={() => onRemove(m.id)} className="text-xs text-gray-400 hover:text-red-500 transition-colors">Remove</button>
      </div>
    </div>
  );
}

function MembersSection({ orgId, members, onRefresh }) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('member');
  const [adding, setAdding] = useState(false);

  async function handleAdd(e) {
    e.preventDefault();
    setAdding(true);
    try {
      await addOrgMember(orgId, { email, role });
      setEmail('');
      onRefresh();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to add member');
    } finally {
      setAdding(false);
    }
  }

  async function handleRemove(memberId) {
    if (!confirm('Remove this member?')) return;
    await removeOrgMember(orgId, memberId);
    onRefresh();
  }

  async function handleRoleChange(memberId, newRole) {
    await updateOrgMember(orgId, memberId, { role: newRole });
    onRefresh();
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Members</h3>

      <form onSubmit={handleAdd} className="flex gap-2 mb-4">
        <input
          type="email"
          placeholder="email@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
        />
        <select value={role} onChange={(e) => setRole(e.target.value)} className="rounded-lg border border-gray-300 px-3 py-2 text-sm">
          <option value="member">Member</option>
          <option value="admin">Admin</option>
          <option value="owner">Owner</option>
        </select>
        <button type="submit" disabled={adding} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50">
          {adding ? 'Adding...' : 'Add'}
        </button>
      </form>

      <div className="divide-y divide-gray-100">
        {members.map((m) => (
          <MemberRow key={m.id} member={m} onRoleChange={handleRoleChange} onRemove={handleRemove} />
        ))}
        {members.length === 0 && <p className="py-3 text-sm text-gray-400">No members yet</p>}
      </div>
    </div>
  );
}

function DomainSection({ orgId, currentDomain, onRefresh }) {
  const [domain, setDomain] = useState(currentDomain || '');
  const [connecting, setConnecting] = useState(false);
  const [result, setResult] = useState(null);
  const [dnsVerified, setDnsVerified] = useState(false);
  const [dnsMethod, setDnsMethod] = useState(null); // 'nameservers' | 'records'
  const [verifying, setVerifying] = useState(false);
  const [verifyMsg, setVerifyMsg] = useState('');

  async function handleConnect(e) {
    e.preventDefault();
    setConnecting(true);
    setResult(null);
    try {
      const res = await connectDomain(orgId, domain);
      setResult(res.data);
      onRefresh();
    } catch (err) {
      setResult({ error: err.response?.data?.error || 'Failed' });
    } finally {
      setConnecting(false);
    }
  }

  async function handleVerifyDns() {
    setVerifying(true);
    setVerifyMsg('');
    try {
      const resp = await fetch(`/api/v1/domains/${currentDomain}/status`, { credentials: 'include' });
      const data = await resp.json();
      if (data.status === 'active' || data.ns_configured || data.nameservers?.length > 0) {
        setDnsVerified(true);
        setVerifyMsg('DNS verified! Your domain is connected.');
      } else {
        setVerifyMsg('DNS changes not detected yet. This can take up to 48 hours.');
      }
    } catch {
      setVerifyMsg('Could not verify DNS. Please try again later.');
    } finally {
      setVerifying(false);
    }
  }

  const d = currentDomain;

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Custom Domain</h3>
      <form onSubmit={handleConnect} className="flex gap-2 mb-3">
        <input
          type="text"
          placeholder="yourcompany.com"
          value={domain}
          onChange={(e) => setDomain(e.target.value)}
          required
          className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
        />
        <button type="submit" disabled={connecting} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50">
          {connecting ? 'Connecting...' : currentDomain ? 'Update' : 'Connect'}
        </button>
      </form>
      {currentDomain && <p className="text-xs text-green-600 mb-2">Current: {currentDomain}</p>}
      {result?.error && <p className="text-xs text-red-600">{result.error}</p>}
      {result?.setup && <p className="text-xs text-green-600">Domain configured successfully</p>}

      {/* DNS configuration instructions when domain is set but not verified */}
      {currentDomain && !dnsVerified && (
        <div className="mt-4 pt-4 border-t border-gray-100">
          <div className="flex items-center gap-2 mb-3">
            <span className="inline-block w-2 h-2 rounded-full bg-amber-400" />
            <span className="text-sm font-medium text-amber-700">DNS setup required</span>
          </div>
          <p className="text-xs text-gray-500 mb-4">Choose how to connect your domain:</p>

          <div className="space-y-3 mb-4">
            {/* Option 1: Nameservers */}
            <button onClick={() => setDnsMethod(dnsMethod === 'nameservers' ? null : 'nameservers')}
              className={`w-full rounded-xl border-2 p-4 text-left transition-all ${
                dnsMethod === 'nameservers' ? 'border-blue-500 bg-blue-50/50 shadow-sm' : 'border-gray-200 hover:border-gray-300'
              }`}>
              <div className="flex items-center gap-3">
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                  dnsMethod === 'nameservers' ? 'border-blue-500' : 'border-gray-300'
                }`}>
                  {dnsMethod === 'nameservers' && <div className="w-2.5 h-2.5 rounded-full bg-blue-500" />}
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">Change nameservers</p>
                  <p className="text-xs text-gray-500 mt-0.5">Manage your entire domain with Jubly (email, calendar, voice, website)</p>
                </div>
              </div>
            </button>

            {dnsMethod === 'nameservers' && (
              <div className="ml-8 p-3 rounded-lg bg-blue-50 border border-blue-200 space-y-1.5">
                <p className="text-xs text-gray-600 mb-2">Update your nameservers at your domain registrar to:</p>
                <div className="font-mono text-sm">
                  <div className="bg-white rounded-lg px-3 py-2 border border-blue-200 text-gray-800 select-all mb-1">ryan.ns.cloudflare.com</div>
                  <div className="bg-white rounded-lg px-3 py-2 border border-blue-200 text-gray-800 select-all">wally.ns.cloudflare.com</div>
                </div>
                <p className="text-[11px] text-gray-500 mt-2">We'll automatically configure all services once nameservers propagate.</p>
              </div>
            )}

            {/* Option 2: DNS Records */}
            <button onClick={() => setDnsMethod(dnsMethod === 'records' ? null : 'records')}
              className={`w-full rounded-xl border-2 p-4 text-left transition-all ${
                dnsMethod === 'records' ? 'border-blue-500 bg-blue-50/50 shadow-sm' : 'border-gray-200 hover:border-gray-300'
              }`}>
              <div className="flex items-center gap-3">
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                  dnsMethod === 'records' ? 'border-blue-500' : 'border-gray-300'
                }`}>
                  {dnsMethod === 'records' && <div className="w-2.5 h-2.5 rounded-full bg-blue-500" />}
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">Add DNS records</p>
                  <p className="text-xs text-gray-500 mt-0.5">Keep your current DNS provider and add Jubly email alongside your existing setup</p>
                </div>
              </div>
            </button>

            {dnsMethod === 'records' && (
              <div className="ml-8 p-3 rounded-lg bg-blue-50 border border-blue-200 space-y-4 text-xs">
                <p className="text-gray-600">Add these records at your DNS provider:</p>
                <div>
                  <p className="text-gray-500 font-semibold mb-1">CNAME records:</p>
                  <div className="overflow-x-auto bg-white rounded-lg border border-blue-200">
                    <table className="w-full font-mono">
                      <thead><tr className="text-gray-500 border-b border-blue-100"><th className="text-left py-1.5 px-2">Name</th><th className="text-left py-1.5 px-2">Value</th></tr></thead>
                      <tbody className="text-gray-700">
                        <tr className="border-b border-blue-50"><td className="py-1.5 px-2 select-all">mail.{d}</td><td className="py-1.5 px-2 select-all">jubly.net</td></tr>
                        <tr className="border-b border-blue-50"><td className="py-1.5 px-2 select-all">calendar.{d}</td><td className="py-1.5 px-2 select-all">jubly.net</td></tr>
                        <tr><td className="py-1.5 px-2 select-all">voice.{d}</td><td className="py-1.5 px-2 select-all">jubly.net</td></tr>
                      </tbody>
                    </table>
                  </div>
                </div>
                <div>
                  <p className="text-gray-500 font-semibold mb-1">MX records (on {d}):</p>
                  <div className="overflow-x-auto bg-white rounded-lg border border-blue-200">
                    <table className="w-full font-mono">
                      <thead><tr className="text-gray-500 border-b border-blue-100"><th className="text-left py-1.5 px-2">Priority</th><th className="text-left py-1.5 px-2">Value</th></tr></thead>
                      <tbody className="text-gray-700">
                        <tr className="border-b border-blue-50"><td className="py-1.5 px-2">10</td><td className="py-1.5 px-2 select-all">route1.mx.cloudflare.net</td></tr>
                        <tr className="border-b border-blue-50"><td className="py-1.5 px-2">20</td><td className="py-1.5 px-2 select-all">route2.mx.cloudflare.net</td></tr>
                        <tr><td className="py-1.5 px-2">30</td><td className="py-1.5 px-2 select-all">route3.mx.cloudflare.net</td></tr>
                      </tbody>
                    </table>
                  </div>
                </div>
                <div>
                  <p className="text-gray-500 font-semibold mb-1">TXT records:</p>
                  <div className="space-y-1 font-mono">
                    <div className="bg-white rounded-lg px-2 py-1.5 border border-blue-200">
                      <span className="text-gray-400">SPF (on {d}):</span>
                      <div className="text-gray-700 select-all break-all">v=spf1 include:_spf.mx.cloudflare.net ~all</div>
                    </div>
                    <div className="bg-white rounded-lg px-2 py-1.5 border border-blue-200">
                      <span className="text-gray-400">DMARC (on _dmarc.{d}):</span>
                      <div className="text-gray-700 select-all break-all">v=DMARC1; p=quarantine; rua=mailto:dmarc@{d}</div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <button onClick={handleVerifyDns} disabled={verifying}
            className="w-full rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50 transition-colors">
            {verifying ? 'Checking DNS...' : "I've made these changes — verify"}
          </button>
          {verifyMsg && (
            <p className={`mt-2 text-xs ${dnsVerified ? 'text-green-600' : 'text-amber-600'}`}>{verifyMsg}</p>
          )}
        </div>
      )}

      {currentDomain && dnsVerified && (
        <div className="mt-4 pt-4 border-t border-gray-100 flex items-center gap-2 text-green-700 text-sm">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
          DNS verified — domain is connected
        </div>
      )}
    </div>
  );
}

function OrgAdmin({ orgId }) {
  const [org, setOrg] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try {
      const res = await getOrg(orgId);
      setOrg(res.data.organization);
      setMembers(res.data.members);
    } catch (err) {
      console.error('Load org error:', err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [orgId]);

  if (loading) return <div className="animate-pulse h-40 bg-gray-100 rounded-xl" />;
  if (!org) return <p className="text-sm text-gray-500">Organization not found</p>;

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Organization Settings</h3>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div><span className="text-gray-500">Name:</span> <span className="font-medium">{org.name}</span></div>
          <div><span className="text-gray-500">Slug:</span> <span className="font-mono">{org.slug}</span></div>
          <div><span className="text-gray-500">Pricing:</span> <span className="font-medium">Usage-based</span></div>
          <div><span className="text-gray-500">Users:</span> <span className="font-medium">Unlimited</span></div>
        </div>
      </div>

      <DomainSection orgId={orgId} currentDomain={org.domain} onRefresh={load} />
      <MembersSection orgId={orgId} members={members} onRefresh={load} />
    </div>
  );
}

const PLANS = ['free', 'business', 'pro', 'enterprise'];

function OrgAddresses({ orgId }) {
  const [addrs, setAddrs] = useState(null);
  useEffect(() => { getOrgAddresses(orgId).then((r) => setAddrs(r.data.addresses)).catch(() => setAddrs([])); }, [orgId]);
  if (addrs === null) return <tr><td colSpan={7} className="px-8 py-2 text-xs text-gray-400">Loading mailboxes…</td></tr>;
  if (!addrs.length) return <tr><td colSpan={7} className="px-8 py-2 text-xs text-gray-400">No mailboxes</td></tr>;
  return (
    <tr><td colSpan={7} className="px-8 py-2 bg-gray-50/60">
      <div className="flex flex-wrap gap-1.5">
        {addrs.map((a) => (
          <span key={a.id} className="inline-flex items-center gap-1 text-xs bg-white border border-gray-200 rounded-full px-2 py-0.5">
            {a.email_address}
            <span className={a.is_verified ? 'text-green-600' : 'text-amber-500'}>{a.is_verified ? '✓' : '•'}</span>
          </span>
        ))}
      </div>
    </td></tr>
  );
}

function SuperAdminPanel() {
  const [orgs, setOrgs] = useState([]);
  const [users, setUsers] = useState([]);
  const [tab, setTab] = useState('orgs');
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [newOrg, setNewOrg] = useState('');
  const [busy, setBusy] = useState('');

  function load() {
    return Promise.all([getPlatformOrgs(), getPlatformUsers()]).then(([orgRes, userRes]) => {
      setOrgs(orgRes.data.organizations);
      setUsers(userRes.data.users);
    });
  }
  useEffect(() => { load().finally(() => setLoading(false)); }, []);

  async function changePlan(orgId, plan) {
    setBusy(orgId);
    try { await updateOrg(orgId, { plan, billing_plan: plan }); await load(); }
    catch (e) { alert(e.response?.data?.error || 'Failed'); }
    finally { setBusy(''); }
  }
  async function toggleStatus(o) {
    const status = o.status === 'active' ? 'suspended' : 'active';
    if (!confirm(`Set ${o.name} to ${status}?`)) return;
    setBusy(o.id);
    try { await updateOrg(o.id, { status }); await load(); }
    catch (e) { alert(e.response?.data?.error || 'Failed'); }
    finally { setBusy(''); }
  }
  async function toggleSuper(u) {
    if (!confirm(`${u.is_super_admin ? 'Revoke' : 'Grant'} super admin for ${u.email}?`)) return;
    setBusy(u.id);
    try { await setSuperAdmin(u.id, !u.is_super_admin); await load(); }
    catch (e) { alert(e.response?.data?.error || 'Failed'); }
    finally { setBusy(''); }
  }
  async function handleCreate(e) {
    e.preventDefault();
    if (!newOrg.trim()) return;
    setBusy('new');
    try { await createPlatformOrg({ name: newOrg.trim() }); setNewOrg(''); await load(); }
    catch (e) { alert(e.response?.data?.error || 'Failed'); }
    finally { setBusy(''); }
  }

  if (loading) return <div className="animate-pulse h-40 bg-gray-100 rounded-xl" />;

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <button onClick={() => setTab('orgs')} className={`px-3 py-1.5 text-sm font-medium rounded-lg ${tab === 'orgs' ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:bg-gray-100'}`}>
          Organizations ({orgs.length})
        </button>
        <button onClick={() => setTab('users')} className={`px-3 py-1.5 text-sm font-medium rounded-lg ${tab === 'users' ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:bg-gray-100'}`}>
          Users ({users.length})
        </button>
      </div>

      {tab === 'orgs' && (
        <>
          <form onSubmit={handleCreate} className="flex gap-2">
            <input value={newOrg} onChange={(e) => setNewOrg(e.target.value)} placeholder="New business account name…"
              className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500" />
            <button type="submit" disabled={busy === 'new'} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50">
              {busy === 'new' ? 'Creating…' : 'Create'}
            </button>
          </form>
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase">
                <tr>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Plan</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Members</th>
                  <th className="px-4 py-3">Mailboxes</th>
                  <th className="px-4 py-3">Domain</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {orgs.map((o) => (
                  <Fragment key={o.id}>
                    <tr className={`hover:bg-gray-50 ${busy === o.id ? 'opacity-50' : ''}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium">{o.name}</div>
                        <div className="font-mono text-[11px] text-gray-400">{o.slug}</div>
                      </td>
                      <td className="px-4 py-3">
                        <select value={o.billing_plan || o.plan || 'free'} onChange={(e) => changePlan(o.id, e.target.value)}
                          className="rounded-lg border border-gray-200 px-2 py-1 text-xs bg-white">
                          {PLANS.map((p) => <option key={p} value={p}>{p}</option>)}
                        </select>
                      </td>
                      <td className="px-4 py-3">
                        <button onClick={() => toggleStatus(o)}
                          className={`text-xs px-2 py-0.5 rounded-full ${o.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                          {o.status || 'active'}
                        </button>
                      </td>
                      <td className="px-4 py-3">{o.member_count}</td>
                      <td className="px-4 py-3">{o.address_count}</td>
                      <td className="px-4 py-3 text-xs">{o.domain || '—'}</td>
                      <td className="px-4 py-3 text-right">
                        <button onClick={() => setExpanded(expanded === o.id ? null : o.id)} className="text-xs text-blue-600 hover:underline">
                          {expanded === o.id ? 'Hide' : 'Mailboxes'}
                        </button>
                      </td>
                    </tr>
                    {expanded === o.id && <OrgAddresses orgId={o.id} />}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tab === 'users' && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase">
              <tr>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Super Admin</th>
                <th className="px-4 py-3">Orgs</th>
                <th className="px-4 py-3">Created</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {users.map((u) => (
                <tr key={u.id} className={`hover:bg-gray-50 ${busy === u.id ? 'opacity-50' : ''}`}>
                  <td className="px-4 py-3 font-mono text-xs">{u.email}</td>
                  <td className="px-4 py-3">{u.display_name}</td>
                  <td className="px-4 py-3">
                    <button onClick={() => toggleSuper(u)}
                      className={`text-xs px-2 py-0.5 rounded-full ${u.is_super_admin ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>
                      {u.is_super_admin ? 'Super Admin' : 'Grant'}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-xs">{(u.organizations || []).map(o => o.org_name).join(', ') || '—'}</td>
                  <td className="px-4 py-3 text-xs text-gray-500">{new Date(u.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function AdminPage() {
  const { user } = useAuth();
  const [selectedOrg, setSelectedOrg] = useState(null);

  const orgs = user?.organizations || [];
  const isAdmin = user?.isSuperAdmin || orgs.some(o => o.role === 'owner' || o.role === 'admin');

  if (!isAdmin) {
    return (
      <div className="max-w-2xl mx-auto p-8 text-center">
        <h1 className="text-xl font-bold text-gray-900 mb-2">Admin Access Required</h1>
        <p className="text-gray-600">You need to be an organization admin or super admin to access this page.</p>
      </div>
    );
  }

  const adminOrgs = orgs.filter(o => o.role === 'owner' || o.role === 'admin');
  const activeOrg = selectedOrg || adminOrgs[0]?.id;

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Manage your organization</h1>
        <p className="text-sm text-gray-500 mt-1">Domain, team members, and settings</p>
      </div>

      {/* Org selector */}
      {adminOrgs.length > 1 && (
        <div className="flex gap-2">
          {adminOrgs.map((o) => (
            <button
              key={o.id}
              onClick={() => setSelectedOrg(o.id)}
              className={`px-4 py-2 text-sm font-medium rounded-lg border ${activeOrg === o.id ? 'bg-blue-50 border-blue-200 text-blue-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
            >
              {o.name}
            </button>
          ))}
        </div>
      )}

      {/* Org admin panel */}
      {activeOrg && <OrgAdmin orgId={activeOrg} />}

      {/* Super admin panel */}
      {user?.isSuperAdmin && (
        <div className="mt-12">
          <div className="border-t border-gray-200 pt-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">Super Admin</span>
              Platform Management
            </h2>
            <SuperAdminPanel />
          </div>
        </div>
      )}
    </div>
  );
}
